// Core chrome (no external deps): a scroll-progress bar + a floating TOC with scroll-spy.
// The TOC collapses via a hover-revealed button; a slim edge tab restores it (persisted).
import { warn } from './util'
import { ICON } from './icons'

const TOC_HIDE_KEY = 'pagelet-toc-collapsed'
const TAB_ID = 'pagelet-toc-tab'

export function mountChrome(): void {
  try {
    progressBar()
  } catch (e) {
    warn('progress', e)
  }
  try {
    buildToc()
  } catch (e) {
    warn('toc', e)
  }
}

function progressBar(): void {
  if (document.getElementById('pagelet-progress')) return
  const bar = document.createElement('div')
  bar.id = 'pagelet-progress'
  document.body.appendChild(bar)
  const update = (): void => {
    const h = document.documentElement
    const max = h.scrollHeight - h.clientHeight
    bar.style.width = (max > 0 ? (h.scrollTop / max) * 100 : 0) + '%'
  }
  document.addEventListener('scroll', update, { passive: true })
  update()
}

function buildToc(): void {
  if (document.getElementById('pagelet-toc')) return
  const prose = document.querySelector('.pagelet-prose')
  if (!prose) return
  const heads = Array.from(prose.querySelectorAll<HTMLElement>('h2, h3'))
  if (heads.length < 3) return

  const nav = document.createElement('nav')
  nav.id = 'pagelet-toc'
  nav.setAttribute('aria-label', 'Table of contents')

  const hideBtn = document.createElement('button')
  hideBtn.className = 'pagelet-toc-hide'
  hideBtn.title = 'Hide index'
  hideBtn.setAttribute('aria-label', 'Hide index')
  hideBtn.innerHTML = ICON.chevronLeft
  hideBtn.addEventListener('click', () => collapseToc(nav))
  nav.appendChild(hideBtn)

  const list = document.createElement('ul')
  const links = new Map<string, HTMLElement>()

  heads.forEach((h, i) => {
    if (!h.id) h.id = `pl-h-${i}`
    const li = document.createElement('li')
    li.className = `lvl-${h.tagName.toLowerCase()}`
    const a = document.createElement('a')
    a.href = `#${h.id}`
    a.textContent = h.textContent || ''
    li.appendChild(a)
    list.appendChild(li)
    links.set(h.id, a)
  })
  nav.appendChild(list)
  document.body.appendChild(nav)

  const obs = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue
        nav.querySelectorAll('a.active').forEach((x) => x.classList.remove('active'))
        links.get((e.target as HTMLElement).id)?.classList.add('active')
      }
    },
    { rootMargin: '0px 0px -75% 0px' },
  )
  heads.forEach((h) => obs.observe(h))

  // Restore a previously collapsed state (mount just the tab).
  if (safeCollapsed()) {
    nav.classList.add('pagelet-toc-hidden')
    mountTab(nav)
  }
}

function safeCollapsed(): boolean {
  try {
    return localStorage.getItem(TOC_HIDE_KEY) === '1'
  } catch {
    return false
  }
}
function storeCollapsed(v: boolean): void {
  try {
    localStorage.setItem(TOC_HIDE_KEY, v ? '1' : '0')
  } catch {
    /* private mode — session-only */
  }
}

function collapseToc(nav: HTMLElement): void {
  storeCollapsed(true)
  nav.classList.add('pagelet-toc-hidden')
  mountTab(nav)
}

function expandToc(nav: HTMLElement): void {
  storeCollapsed(false)
  document.getElementById(TAB_ID)?.remove()
  nav.classList.remove('pagelet-toc-hidden')
  // Restart the enter animation.
  nav.classList.remove('pagelet-toc-enter')
  void nav.offsetWidth
  nav.classList.add('pagelet-toc-enter')
}

/** The slim left-edge tab shown while the TOC is collapsed. */
function mountTab(nav: HTMLElement): void {
  if (document.getElementById(TAB_ID)) return
  const tab = document.createElement('button')
  tab.id = TAB_ID
  tab.title = 'Show index'
  tab.setAttribute('aria-label', 'Show index')
  tab.innerHTML = `${ICON.panelLeft}<span class="pagelet-toc-tab-label">Index</span>`
  tab.addEventListener('click', () => expandToc(nav))
  document.body.appendChild(tab)
}
